import React, { useCallback, useEffect, useState } from "react";
import NavHeader from "../nav/NavHeader";
import SideMenu from "../nav/Sidemenu";
import PersonaManagementTable from "../personaRegistrations/PersonaManagementTable";
import PersonaRegistrationsForm from "../personaRegistrations/PersonaRegistrationsForm";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import "../../assets/css/reportRegistrations.css";

const PersonaAdministration = () => {
    const [activeTab, setActiveTab] = useState("management");
    const [searchText, setSearchText] = useState("");
    const [personas, setPersonas] = useState([]);
    const [isPersonasLoading, setIsPersonasLoading] = useState(true);
    const [personasError, setPersonasError] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editPersonaData, setEditPersonaData] = useState(null);
    const [isUpdatingPersona, setIsUpdatingPersona] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePersonaData, setDeletePersonaData] = useState(null);
    const [deletingPersonaId, setDeletingPersonaId] = useState(null);
    const [toastState, setToastState] = useState({
        show: false,
        type: "success",
        message: "",
    });
    const [showMapReportModal, setShowMapReportModal] = useState(false);
    const [selectedPersonaForMapping, setSelectedPersonaForMapping] = useState(null);
    const [selectedReportIds, setSelectedReportIds] = useState([]);
    const [mapReportSearchText, setMapReportSearchText] = useState("");
    const [isSavingReportMapping, setIsSavingReportMapping] = useState(false);
    const [availableReports, setAvailableReports] = useState([]);
    const [isReportsOptionsLoading, setIsReportsOptionsLoading] = useState(true);
    const [reportsOptionsError, setReportsOptionsError] = useState("");

    const passtoken =
        !localStorage.getItem("access-token-storage") ||
        localStorage.getItem("access-token-storage") === null
            ? null
            : JSON.parse(localStorage.getItem("access-token-storage"))["accessToken"];

    const personasEndpoint = `${process.env.REACT_APP_SERVER_URL}/aipd/persona-administration/persona-management`;
    const reportsOptionsEndpoint = `${process.env.REACT_APP_SERVER_URL}/aipd/report-management/reports`;
    const mapReportsEndpoint = `${process.env.REACT_APP_SERVER_URL}/aipd/persona-administration/map-reports-admin`;

    const showToast = (message, type = "success") => {
        setToastState({
            show: true,
            type,
            message,
        });
    };

    const formatNow = () => {
        const now = new Date();
        const pad = (value) => String(value).padStart(2, "0");
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
            now.getDate()
        )} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    };

    const normalizePersonasPayload = (payload) => {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (Array.isArray(payload?.data?.personas)) {
            return payload.data.personas;
        }

        if (payload?.data && Array.isArray(payload.data)) {
            return payload.data;
        }

        if (payload?.data && typeof payload.data === "object") {
            return [payload.data];
        }

        if (Array.isArray(payload?.personas)) {
            return payload.personas;
        }

        if (payload && typeof payload === "object" && (payload.userGroupId || payload.user_group_id)) {
            return [payload];
        }

        return [];
    };

    const normalizeReportsPayload = (payload) => {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (Array.isArray(payload?.data?.reports)) {
            return payload.data.reports;
        }

        if (payload?.data && Array.isArray(payload.data)) {
            return payload.data;
        }

        if (Array.isArray(payload?.reports)) {
            return payload.reports;
        }

        return [];
    };

    const formatPersonaCreatedAt = (createdAt) => {
        if (createdAt === null || createdAt === undefined || createdAt === "") {
            return "-";
        }

        if (typeof createdAt === "number") {
            const date = new Date(createdAt);
            return Number.isNaN(date.getTime()) ? String(createdAt) : date.toLocaleString();
        }

        return String(createdAt);
    };

    const fetchPersonas = useCallback(async () => {
        setIsPersonasLoading(true);
        setPersonasError("");

        try {
            const headers = {
                Accept: "application/json",
            };

            if (passtoken) {
                headers["oidc-token"] = passtoken;
            }

            const response = await fetch(personasEndpoint, { headers });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json?.message || "Failed to load personas.");
            }

            const fetchedPersonas = normalizePersonasPayload(json).map((persona) => {
                const reportIdsFromMapped = Array.isArray(persona.mappedReportIds)
                    ? persona.mappedReportIds
                    : [];
                const reportIdsFromReports = Array.isArray(persona.reports)
                    ? persona.reports
                          .map((report) =>
                              report?.reportId ?? report?.reportID ?? report?.report_id ?? report?.id
                          )
                          .filter((reportId) => reportId !== undefined && reportId !== null)
                    : [];

                const mappedReportIds = (reportIdsFromReports.length
                    ? reportIdsFromReports
                    : reportIdsFromMapped
                ).map((reportId) => {
                    const parsedValue = Number(reportId);
                    return Number.isNaN(parsedValue) ? reportId : parsedValue;
                });

                return {
                    id: persona.userGroupId ?? persona.user_group_id ?? persona.id,
                    dataGroupId: persona.dataGroupId ?? persona.data_group_id,
                    name: persona.personaName || persona.persona_name || persona.name || "-",
                    createdBy: persona.createdBy || persona.create_by || persona.createdby || "-",
                    createdAt: formatPersonaCreatedAt(persona.createdAt ?? persona.created_at),
                    mappedReportIds,
                };
            });

            setPersonas(fetchedPersonas);
        } catch (error) {
            setPersonas([]);
            setPersonasError(error?.message || "Unable to fetch personas at the moment.");
        } finally {
            setIsPersonasLoading(false);
        }
    }, [passtoken, personasEndpoint]);

    const fetchReportsOptions = useCallback(async () => {
        setIsReportsOptionsLoading(true);
        setReportsOptionsError("");

        try {
            const headers = {
                Accept: "application/json",
            };

            if (passtoken) {
                headers["oidc-token"] = passtoken;
            }

            const response = await fetch(reportsOptionsEndpoint, { headers });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json?.message || "Failed to load reports.");
            }

            const fetchedReports = normalizeReportsPayload(json).map((report) => ({
                id: report.reportId ?? report.reportID,
                name: report.reportName || report.report_name || "-",
            }));

            setAvailableReports(fetchedReports.filter((report) => report.id !== undefined && report.id !== null));
        } catch (error) {
            setAvailableReports([]);
            setReportsOptionsError(error?.message || "Unable to fetch reports at the moment.");
        } finally {
            setIsReportsOptionsLoading(false);
        }
    }, [passtoken, reportsOptionsEndpoint]);

    useEffect(() => {
        fetchPersonas();
    }, [fetchPersonas]);

    useEffect(() => {
        fetchReportsOptions();
    }, [fetchReportsOptions]);

    useEffect(() => {
        if (!toastState.show) {
            return undefined;
        }

        const timer = setTimeout(() => {
            setToastState((prevState) => ({ ...prevState, show: false }));
        }, 3000);

        return () => clearTimeout(timer);
    }, [toastState.show]);

    const filteredPersonas = personas.filter((persona) =>
        persona.name.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleCreatePersona = (name) => {
        const isDuplicate = personas.some(
            (persona) => persona.name.toLowerCase() === name.toLowerCase()
        );

        if (isDuplicate) {
            return;
        }

        const createdBy =
            localStorage.getItem("userName") || localStorage.getItem("myemail") || "Current User";

        setPersonas((prevData) => [
            ...prevData,
            { id: Date.now(), name, createdBy, createdAt: formatNow(), mappedReportIds: [] },
        ]);
        setActiveTab("management");
    };

    const handleOpenDeleteModal = (persona) => {
        if (!persona || deletingPersonaId !== null) {
            return;
        }

        setDeletePersonaData({
            id: persona.id,
            dataGroupId: persona.dataGroupId,
            name: persona.name,
        });
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        if (deletingPersonaId !== null) {
            return;
        }

        setShowDeleteModal(false);
        setDeletePersonaData(null);
    };

    const handleDeletePersona = async () => {
        const userGroupId = deletePersonaData?.id;
        const dataGroupId = deletePersonaData?.dataGroupId;

        if (!userGroupId || deletingPersonaId !== null) {
            return;
        }

        if (dataGroupId === undefined || dataGroupId === null) {
            showToast("Required persona identifiers are missing.", "danger");
            return;
        }

        setDeletingPersonaId(userGroupId);

        try {
            const headers = {
                Accept: "application/json",
            };

            if (passtoken) {
                headers["oidc-token"] = passtoken;
            }

            const response = await fetch(
                `${personasEndpoint}/${userGroupId}/${dataGroupId}`,
                {
                    method: "DELETE",
                    headers,
                }
            );

            if (response.status === 204) {
                setPersonas((prevData) => prevData.filter((persona) => persona.id !== userGroupId));
                showToast("Persona deleted successfully.", "success");
                setShowDeleteModal(false);
                setDeletePersonaData(null);
                return;
            }

            if (response.status === 404) {
                throw new Error("Persona not found.");
            }

            const responseText = await response.text();
            let responseJson;

            try {
                responseJson = responseText ? JSON.parse(responseText) : {};
            } catch (parseError) {
                responseJson = { message: responseText || "Failed to delete persona." };
            }

            console.log("Delete Persona Response:", response, responseJson);
            if (!response.ok) {
                throw new Error(
                    responseJson?.message ||
                        responseJson?.error ||
                        responseJson?.data?.message ||
                        "Failed to delete persona."
                );
            }

            setPersonas((prevData) => prevData.filter((persona) => persona.id !== userGroupId));
            showToast("Persona deleted successfully.", "success");
            setShowDeleteModal(false);
            setDeletePersonaData(null);
        } catch (error) {
            setShowDeleteModal(false);
            setDeletePersonaData(null);
            showToast(
                error?.message || "Unable to delete persona at the moment.",
                "danger"
            );
        } finally {
            setDeletingPersonaId(null);
        }
    };

    const handleEditPersona = (persona) => {
        setEditPersonaData(persona);
        setShowEditModal(true);
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditPersonaData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditPersonaData(null);
    };

    const handleUpdatePersona = (event) => {
        event.preventDefault();

        if (!editPersonaData) {
            return;
        }

        const personaName = String(editPersonaData.name || "").trim();

        if (!personaName) {
            showToast("Persona Name is required.", "danger");
            return;
        }

        const userGroupId = editPersonaData.id;
        const dataGroupId = editPersonaData.dataGroupId;

        if (userGroupId === undefined || userGroupId === null || dataGroupId === undefined || dataGroupId === null) {
            showToast("Required persona identifiers are missing.", "danger");
            return;
        }

        const updatePersona = async () => {
            setIsUpdatingPersona(true);

            try {
                const headers = {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                };

                if (passtoken) {
                    headers["oidc-token"] = passtoken;
                }

                const response = await fetch(
                    `${personasEndpoint}/${userGroupId}/${dataGroupId}`,
                    {
                        method: "PUT",
                        headers,
                        body: JSON.stringify({ persona_name: personaName }),
                    }
                );

                const responseJson = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(responseJson?.message || "Failed to update persona.");
                }

                setPersonas((prevData) =>
                    prevData.map((persona) =>
                        persona.id === editPersonaData.id
                            ? { ...persona, name: personaName }
                            : persona
                    )
                );

                showToast("Persona updated successfully.", "success");
                handleCloseEditModal();
            } catch (error) {
                showToast(
                    error?.message || "Unable to update persona at the moment.",
                    "danger"
                );
            } finally {
                setIsUpdatingPersona(false);
            }
        };

        updatePersona();
    };

    const handleAddReport = (persona) => {
        setSelectedPersonaForMapping(persona);
        setSelectedReportIds(persona.mappedReportIds || []);
        setMapReportSearchText("");
        setShowMapReportModal(true);
    };

    const handleCloseMapReportModal = () => {
        if (isSavingReportMapping) {
            return;
        }

        setShowMapReportModal(false);
        setSelectedPersonaForMapping(null);
        setSelectedReportIds([]);
        setMapReportSearchText("");
    };

    const handleToggleReport = (reportId) => {
        setSelectedReportIds((prevIds) =>
            prevIds.includes(reportId)
                ? prevIds.filter((id) => id !== reportId)
                : [...prevIds, reportId]
        );
    };

    const filteredAvailableReports = availableReports.filter((report) =>
        report.name.toLowerCase().includes(mapReportSearchText.toLowerCase())
    );

    const handleSelectAllVisibleReports = () => {
        const visibleIds = filteredAvailableReports.map((report) => report.id);
        setSelectedReportIds((prevIds) => {
            const merged = new Set([...prevIds, ...visibleIds]);
            return Array.from(merged);
        });
    };

    const handleClearVisibleReports = () => {
        const visibleIds = new Set(filteredAvailableReports.map((report) => report.id));
        setSelectedReportIds((prevIds) =>
            prevIds.filter((reportId) => !visibleIds.has(reportId))
        );
    };

    const handleSaveReportMapping = (event) => {
        event.preventDefault();

        if (!selectedPersonaForMapping) {
            return;
        }

        const userGroupId = selectedPersonaForMapping.id;
        const dataGroupId = selectedPersonaForMapping.dataGroupId;

        if (userGroupId === undefined || userGroupId === null || dataGroupId === undefined || dataGroupId === null) {
            showToast("Required persona identifiers are missing.", "danger");
            return;
        }

        const payloadReportIds = selectedReportIds.map((reportId) => {
            const parsedValue = Number(reportId);
            return Number.isNaN(parsedValue) ? reportId : parsedValue;
        });

        const saveReportMapping = async () => {
            setIsSavingReportMapping(true);

            try {
                const headers = {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                };

                if (passtoken) {
                    headers["oidc-token"] = passtoken;
                }

                const response = await fetch(mapReportsEndpoint, {
                    method: "POST",
                    headers,
                    body: JSON.stringify({
                        report_id: payloadReportIds,
                        data_group_id: dataGroupId,
                        user_group_id: userGroupId,
                    }),
                });

                const responseJson = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(responseJson?.message || "Failed to map reports.");
                }

                setPersonas((prevData) =>
                    prevData.map((persona) =>
                        persona.id === selectedPersonaForMapping.id
                            ? { ...persona, mappedReportIds: selectedReportIds }
                            : persona
                    )
                );

                showToast("Reports mapped successfully.", "success");
                handleCloseMapReportModal();
            } catch (error) {
                showToast(
                    error?.message || "Unable to map reports at the moment.",
                    "danger"
                );
            } finally {
                setIsSavingReportMapping(false);
            }
        };

        saveReportMapping();
    };

    return (
        <div>
            {toastState.show && (
                <div
                    style={{
                        position: "fixed",
                        top: "16px",
                        right: "16px",
                        zIndex: 99999,
                        minWidth: "320px",
                        maxWidth: "420px",
                    }}
                >
                    <div
                        className={`alert alert-${toastState.type} shadow-sm mb-0`}
                        role="alert"
                    >
                        {toastState.message}
                    </div>
                </div>
            )}

            <NavHeader />
            <SideMenu />
            <div style={{ position: "relative" }} id="main">
                <div className="container-fluid pt-3 pb-4">
                    <h1 className="mb-1">Persona Administration</h1>
                    <p className="text-muted mb-0">
                        Manage and register personas for your application.
                    </p>
                </div>

                <div className="container-fluid pb-4">
                    <div className="rr-tabsWrap">
                        <button
                            type="button"
                            onClick={() => setActiveTab("management")}
                            className={`rr-tabBtn ${activeTab === "management" ? "active" : ""}`}
                        >
                            Persona Management
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("registrations")}
                            className={`rr-tabBtn ${
                                activeTab === "registrations" ? "active" : ""
                            }`}
                        >
                            Persona Registration
                        </button>
                    </div>
                </div>

                {activeTab === "management" ? (
                    <PersonaManagementTable
                        searchText={searchText}
                        setSearchText={setSearchText}
                        filteredPersonas={filteredPersonas}
                        isLoading={isPersonasLoading}
                        errorMessage={personasError}
                        deletingPersonaId={deletingPersonaId}
                        onDeletePersona={handleOpenDeleteModal}
                        onEditPersona={handleEditPersona}
                        onAddReport={handleAddReport}
                    />
                ) : (
                    <PersonaRegistrationsForm onCreatePersona={handleCreatePersona} />
                )}

                <Dialog
                    open={Boolean(showEditModal && editPersonaData)}
                    onClose={handleCloseEditModal}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ className: "rr-manageCard rr-editDialogPaper" }}
                >
                    <DialogTitle disableTypography className="border-0 rr-deleteModalHeader rr-deleteModalHeaderFlex">
                        <h5 className="modal-title mb-0">Edit Persona</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={handleCloseEditModal}
                        ></button>
                    </DialogTitle>

                    <form onSubmit={handleUpdatePersona}>
                        <DialogContent className="pt-3">
                            <label className="form-label rr-label">Persona Name</label>
                            <input
                                type="text"
                                name="name"
                                className="form-control rr-input"
                                value={editPersonaData?.name || ""}
                                onChange={handleEditChange}
                                required
                            />
                        </DialogContent>

                        <DialogActions className="border-0 pt-2">
                            <button
                                type="button"
                                className="btn rr-btnGhost rr-deleteModalActionBtn"
                                onClick={handleCloseEditModal}
                                disabled={isUpdatingPersona}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn rr-btnPrimary rr-deleteModalActionBtn"
                                disabled={isUpdatingPersona}
                            >
                                {isUpdatingPersona ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Updating...
                                    </>
                                ) : (
                                    "Update"
                                )}
                            </button>
                        </DialogActions>
                    </form>
                </Dialog>

                <Dialog
                    open={Boolean(showDeleteModal && deletePersonaData)}
                    onClose={handleCloseDeleteModal}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ className: "rr-manageCard rr-deleteDialogPaper" }}
                >
                    <DialogTitle disableTypography className="border-0 rr-deleteModalHeader rr-deleteModalHeaderFlex">
                        <h5 className="modal-title mb-0">Confirm Deletion</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={handleCloseDeleteModal}
                            disabled={deletingPersonaId !== null}
                        ></button>
                    </DialogTitle>

                    <DialogContent className="pt-3">
                        <p className="mb-0 rr-deleteModalText">
                            Are you sure you want to delete persona
                            {" "}
                            <strong>{deletePersonaData?.name || "this persona"}</strong>
                            ? This action cannot be undone.
                        </p>
                    </DialogContent>

                    <DialogActions className="border-0 pt-2">
                        <button
                            type="button"
                            className="btn rr-btnGhost rr-deleteModalActionBtn"
                            onClick={handleCloseDeleteModal}
                            disabled={deletingPersonaId !== null}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn rr-btnPrimary rr-deleteModalActionBtn"
                            onClick={handleDeletePersona}
                            disabled={deletingPersonaId !== null}
                        >
                            {String(deletingPersonaId) === String(deletePersonaData?.id) ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={Boolean(showMapReportModal && selectedPersonaForMapping)}
                    onClose={handleCloseMapReportModal}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ className: "rr-manageCard rr-editDialogPaper" }}
                >
                    <DialogTitle disableTypography className="border-0 rr-deleteModalHeader rr-deleteModalHeaderFlex">
                        <h5 className="modal-title mb-0">
                            Map Reports - {selectedPersonaForMapping?.name || ""}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={handleCloseMapReportModal}
                            disabled={isSavingReportMapping}
                        ></button>
                    </DialogTitle>

                    <form onSubmit={handleSaveReportMapping}>
                        <DialogContent className="pt-3">
                            <p className="text-muted mb-2">
                                Select one or more reports to map with this persona.
                            </p>

                            <div className="rr-mapToolbar mb-3">
                                <input
                                    type="text"
                                    className="form-control rr-manageSearch"
                                    placeholder="Search reports"
                                    value={mapReportSearchText}
                                    onChange={(event) =>
                                        setMapReportSearchText(event.target.value)
                                    }
                                />
                                <div className="rr-mapActions">
                                    <span className="rr-mapCount">
                                        Selected: {selectedReportIds.length}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn rr-btnGhost rr-mapMiniBtn"
                                        onClick={handleSelectAllVisibleReports}
                                        disabled={
                                            isSavingReportMapping ||
                                            isReportsOptionsLoading ||
                                            filteredAvailableReports.length === 0
                                        }
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        className="btn rr-btnGhost rr-mapMiniBtn"
                                        onClick={handleClearVisibleReports}
                                        disabled={
                                            isSavingReportMapping ||
                                            isReportsOptionsLoading ||
                                            filteredAvailableReports.length === 0
                                        }
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            <div className="rr-mapReportList">
                                {isReportsOptionsLoading ? (
                                    <div className="text-muted py-2">Loading reports...</div>
                                ) : reportsOptionsError ? (
                                    <div className="text-danger py-2">Unable to load reports. {reportsOptionsError}</div>
                                ) : filteredAvailableReports.length === 0 ? (
                                    <div className="text-muted py-2">
                                        No reports found for this search.
                                    </div>
                                ) : (
                                    filteredAvailableReports.map((report) => (
                                        <label
                                            key={report.id}
                                            className="rr-mapReportItem"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedReportIds.includes(report.id)}
                                                disabled={isSavingReportMapping}
                                                onChange={() => handleToggleReport(report.id)}
                                            />
                                            <span>{report.name}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </DialogContent>

                        <DialogActions className="border-0 pt-2">
                            <button
                                type="button"
                                className="btn rr-btnGhost rr-deleteModalActionBtn"
                                onClick={handleCloseMapReportModal}
                                disabled={isSavingReportMapping}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn rr-btnPrimary rr-deleteModalActionBtn"
                                disabled={isSavingReportMapping}
                            >
                                {isSavingReportMapping ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Saving...
                                    </>
                                ) : (
                                    "Save Mapping"
                                )}
                            </button>
                        </DialogActions>
                    </form>
                </Dialog>
            </div>
        </div>
    );
};

export default PersonaAdministration;
